import { useEffect, useMemo, useState } from "react";
import {
  Box,
  FormControl,
  FormControlLabel,
  Grid,
  makeStyles,
  Radio,
  RadioGroup,
  withStyles,
  Checkbox,
  Typography,
} from "@material-ui/core";

import classNames from "classnames";
import DatePicker from "react-multi-date-picker";

import { COLORS } from "../../../../../theme/colors";
import { formatDatetime } from "../../../../../services";
import { TextInput } from "../../../../../components/base";
import { Icon } from "../../../../../components/base/Icon";
import { SwitchButton } from "../../../../../components/SwitchButton";
import user from "../../../../../assets/images/icons/user.svg";
import phone from "../../../../../assets/images/icons/phone.svg";
import email from "../../../../../assets/images/icons/email.svg";
import date from "../../../../../assets/images/icons/workspace/date.svg";

const PurpleRadio = withStyles({
  root: {
    color: COLORS.purple,
  },
})((props) => <Radio color="default" {...props} />);

const useStyles = makeStyles(() => ({
  heading: {
    fontSize: 30,
    fontWeight: 700,
    marginBottom: 30,
  },
  topBox: {
    marginBottom: 30,
  },
  Input: {
    width: 270,
    marginTop: 6,
    background: COLORS.white,
  },
  paper: {
    fontSize: 18,
    marginTop: 14,
  },
  radioLabel: {
    fontSize: 18,
  },
  checkDiv: {
    display: "flex",
  },
  checkStyle: {
    marginLeft: 10,
    marginRight: 5,
    "& .MuiSvgIcon-root": {
      fill: COLORS.purple,
    },
  },
  toggleBtn: {
    marginTop: 16,
    marginLeft: 10,
  },
  dueDateDiv: {
    marginTop: 20,
    paddingBottom: 20,
  },
  dueDate: {
    display: "flex",
    "&>:nth-child(1)": {
      borderRadius: "5px 0px 0px 5px",
    },
    "&>:nth-child(3)": {
      borderRadius: "0px 5px 5px 0px",
    },
  },
  dueDateBox: {
    width: 100,
    background: COLORS.gray[0],
    display: "flex",
    justifyContent: "center",
    paddingTop: 8,
    paddingBottom: 8,
    fontSize: 18,
    color: COLORS.white,
    borderRight: `2px solid ${COLORS.white}`,
    "&:hover": {
      background: COLORS.purple,
    },
    cursor: "pointer",
  },
  activeDueDateBox: {
    background: COLORS.purple,
  },
  rightDiv: {
    marginLeft: 26,
  },
  toggleText: {
    fontSize: 14,
  },
  togglePaperText: {
    color: COLORS.darks[0][50],
    fontSize: 18,
    marginTop: 10,
  },
  calendarIcon: {
    marginTop: 20,
  },
  dateStyle: {
    "& .rmdp-day.rmdp-disabled": {
      color: COLORS.darks[0][50],
    },
    "& .rmdp-week-day": {
      color: COLORS.black,
      fontWeight: 800,
    },
    "& .rmdp-day:not(.rmdp-disabled):not(.rmdp-day-hidden) span:hover": {
      background: COLORS.purple,
    },
    "& .rmdp-day.rmdp-selected span:not(.highlight)": {
      background: COLORS.purple,
    },
  },
}));

const PERSON_TYPES = {
  in_charge: 0,
  switch: 1,
};

const getOperators = (building) => {
  let personInCharge, switchingPerson;
  if (building.operators.length < 2) {
    personInCharge = building.operators[0];
    switchingPerson = null;
  } else {
    personInCharge = building.operators.find((o) => !o.is_responsible_for_switching);
    switchingPerson = building.operators.find((o) => o.is_responsible_for_switching);
  }
  if (!personInCharge) {
    personInCharge = { is_responsible_for_switching: true };
  }
  return [personInCharge, switchingPerson];
};

const Cordinators = ({ building, onBuildingChange }) => {
  const classes = useStyles();

  const [personInCharge, switchingPerson] = getOperators(building);
  const [savedPersonInCharge, setSavePersonInCharge] = useState();
  const [value, setValue] = useState(building.company_name ? "external" : "internal");
  const [state, setState] = useState({ toggle: true });
  const [days, setDays] = useState(
    personInCharge?.onboarding_due_date ? null : personInCharge.days || 15
  );
  const [dueDate, setDueDate] = useState(personInCharge?.onboarding_due_date);
  const [dueDateDisplay, setDueDateDisplay] = useState();

  const savedDueDate = useMemo(() => {
    let d = dueDate;
    if (d && typeof dueDate !== "string") {
      d = `${dueDate.year}-${dueDate.month}-${dueDate.day}`;
    }
    return d;
  }, [dueDate]);

  useEffect(() => {
    if (!savedPersonInCharge || building.is_done) {
      setSavePersonInCharge(personInCharge);
    }
  }, [personInCharge, building.is_done]);

  useEffect(() => {
    if (dueDate && typeof dueDate !== "string") {
      setDueDateDisplay(`${dueDate.day} ${dueDate.month.shortName} ${dueDate.year}`);
    } else if (dueDate) {
      setDueDateDisplay(formatDatetime(dueDate, "DD MMM YYYY"));
    }
  }, [dueDate]);

  useEffect(() => {
    onBuildingChange(
      "operators",
      building.operators.map((o) => ({
        ...o,
        days: days,
        onboarding_due_date: savedDueDate,
      }))
    );
  }, [days, savedDueDate]);

  const handleTenantSwitchToggle = (e) => {
    const checked = e.target.checked;
    setState((prev) => {
      return {
        ...prev,
        toggle: checked,
      };
    });
    if (!checked) {
      onBuildingChange("operators", personInCharge ? [personInCharge] : []);
    }
  };

  const onOperatorChange = (type, key, value) => {
    let operators;
    if (type === PERSON_TYPES.in_charge) {
      operators = [{ ...personInCharge, [key]: value }, switchingPerson];
    } else {
      operators = [
        personInCharge,
        {
          ...switchingPerson,
          [key]: value,
          is_responsible_for_switching: true,
        },
      ];
    }
    onBuildingChange(
      "operators",
      operators
        .filter((o) => !!o)
        .map((o) => ({ ...o, days: days, onboarding_due_date: savedDueDate }))
    );
  };

  return (
    <Box>
      <Typography className={classes.heading}>
        Who should be responsible for the onboarding?
      </Typography>
      <Box className={classes.topBox}>
        <FormControl component="fieldset">
          <RadioGroup value={value} onChange={(e) => setValue(e.target.value)}>
            <FormControlLabel
              value="internal"
              control={<PurpleRadio />}
              classes={{ label: classes.radioLabel }}
              label="Your company"
            />
            <Box className={classes.radioBox}>
              <FormControlLabel
                value="external"
                control={<PurpleRadio />}
                classes={{ label: classes.radioLabel }}
                label="External company"
              />
              {value === "external" && (
                <Grid container spacing={3}>
                  <Grid item xs={5}>
                    <Typography className={classes.paper}>Company name:</Typography>
                  </Grid>
                  <Grid item xs={5} className={classes.rightDiv}>
                    <Box className={classes.Input}>
                      <TextInput
                        fullWidth
                        value={building.company_name}
                        onChange={(v) => onBuildingChange("company_name", v)}
                        style={{ background: COLORS.white }}
                        type="text"
                        containerStyle={classes.box}
                      />
                    </Box>
                  </Grid>
                </Grid>
              )}
            </Box>
          </RadioGroup>
        </FormControl>
      </Box>
      <Grid container spacing={3}>
        <Grid item xs={3}>
          <Typography className={classes.paper}>Person in charge:</Typography>
        </Grid>
        <Grid item xs={9}>
          <Box className={classes.iconText}>
            <Box className={classes.checkDiv}>
              <TextInput
                fullWidth
                placeHolder="Name"
                value={personInCharge?.username}
                disabled={savedPersonInCharge?.username}
                onChange={(v) => onOperatorChange(PERSON_TYPES.in_charge, "username", v)}
                containerStyle={classes.Input}
                startAdornment={<img src={user} />}
              />
              <FormControlLabel
                style={{ marginRight: 0 }}
                control={
                  <Checkbox
                    checked={personInCharge?.is_responsible_for_switching}
                    onChange={(e) =>
                      onOperatorChange(
                        PERSON_TYPES.in_charge,
                        "is_responsible_for_switching",
                        e.target.checked
                      )
                    }
                    className={classes.checkStyle}
                    inputProps={{ "aria-label": "primary checkbox" }}
                  />
                }
                label={
                  <Typography variant="h4">
                    is responsible also for &quot;switching tenant&quot;
                  </Typography>
                }
              />
            </Box>
            <TextInput
              fullWidth
              placeHolder="Email"
              value={personInCharge?.email}
              disabled={savedPersonInCharge?.email}
              onChange={(v) => onOperatorChange(PERSON_TYPES.in_charge, "email", v)}
              containerStyle={classes.Input}
              startAdornment={<img src={email} />}
            />
            <TextInput
              fullWidth
              placeHolder="Mobile number"
              value={personInCharge?.phone}
              disabled={savedPersonInCharge?.phone}
              onChange={(v) => onOperatorChange(PERSON_TYPES.in_charge, "phone", v)}
              containerStyle={classes.Input}
              startAdornment={<img src={phone} />}
            />
          </Box>
        </Grid>
      </Grid>
      {!personInCharge.is_responsible_for_switching && (
        <>
          <Box display="flex" mt={3} mb={1}>
            <Typography className={state.toggle === true ? classes.paper : classes.togglePaperText}>
              I know who is in charge for switching-tenants
            </Typography>
            <SwitchButton checked={state.toggle} handleChange={handleTenantSwitchToggle} />
          </Box>
          {state.toggle ? (
            <Grid container spacing={3}>
              <Grid item xs={3}>
                <Typography className={classes.paper}></Typography>
              </Grid>
              <Grid item xs={5}>
                <Box className={classes.iconText}>
                  <TextInput
                    fullWidth
                    placeHolder="Name"
                    value={switchingPerson?.username}
                    onChange={(v) => onOperatorChange(PERSON_TYPES.switch, "username", v)}
                    containerStyle={classes.Input}
                    startAdornment={<img src={user} />}
                  />
                  <TextInput
                    fullWidth
                    placeHolder="Email"
                    value={switchingPerson?.email}
                    onChange={(v) => onOperatorChange(PERSON_TYPES.switch, "email", v)}
                    containerStyle={classes.Input}
                    startAdornment={<img src={email} />}
                  />
                  <TextInput
                    fullWidth
                    placeHolder="Mobile Number"
                    value={switchingPerson?.phone}
                    onChange={(v) => onOperatorChange(PERSON_TYPES.switch, "phone", v)}
                    containerStyle={classes.Input}
                    startAdornment={<img src={phone} />}
                  />
                </Box>
              </Grid>
            </Grid>
          ) : (
            <Typography className={classes.toggleText}>
              OK, we will figure this at the end of the onboarding
            </Typography>
          )}
        </>
      )}
      <Box className={classes.dueDateDiv}>
        <Grid container spacing={3} alignItems="center">
          <Grid item xs={3}>
            <Typography variant="h4">Onboarding due date:</Typography>
          </Grid>
          <Grid item xs={4}>
            <Box className={classes.dueDate}>
              {[15, 30, 45].map((d, i) => (
                <Typography
                  key={i}
                  className={classNames(days === d && classes.activeDueDateBox, classes.dueDateBox)}
                  onClick={() => {
                    setDays(d);
                    setDueDateDisplay();
                  }}
                >
                  in {d}d
                </Typography>
              ))}
            </Box>
          </Grid>

          <DatePicker
            render={(value, openCalendar) => {
              return <Icon src={date} hoverable onClick={openCalendar} />;
            }}
            calendarPosition="top-right"
            fixMainPosition
            minDate={new Date()}
            numberOfMonths={1}
            onChange={(v) => {
              setDueDate(v);
              setDays(null);
            }}
            weekStartDayIndex={1}
            className={classes.dateStyle}
          />

          {dueDateDisplay && <Typography style={{ marginLeft: 8 }}>{dueDateDisplay}</Typography>}
        </Grid>
      </Box>
    </Box>
  );
};

export default Cordinators;
